import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Coffee, MapPin, ShoppingBag, Calendar, Home as HomeIcon, 
  Sparkles, ArrowRight, Clock, Heart, Users, ClipboardList,
  Utensils, Gamepad2, Smartphone
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

// Two people walking arm in arm in snowy park
const SERVICE_HERO = "https://images.unsplash.com/photo-1768192655316-22a5eee66e28?w=800&auto=format&fit=crop&q=80";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const services = [
  {
    icon: Coffee,
    title: 'Companionship Visits at Home',
    description: 'Warm, friendly visits in the comfort of your own home. Our companions provide engaging conversation, a listening ear, and genuine human connection.',
    features: ['Regular scheduled visits', 'Flexible timing', 'Personalised conversation', 'Emotional support'],
    image: 'https://images.unsplash.com/photo-1694857561361-b66b3d1a6cc8?w=500&auto=format&fit=crop&q=80'
  },
  {
    icon: MapPin,
    title: 'Social Outings & Walks',
    description: 'Accompanied trips to local parks, cafes, community events, and favourite places. Enjoy fresh air and social engagement with trusted support.',
    features: ['Local park visits', 'Café and tea outings', 'Community events', 'Gentle exercise walks'],
    image: 'https://images.unsplash.com/photo-1768192655316-22a5eee66e28?w=500&auto=format&fit=crop&q=80'
  },
  {
    icon: Calendar,
    title: 'Appointment Accompaniment',
    description: 'Supportive presence at appointments, providing reassurance and friendly company. We help reduce the stress of attending important appointments alone.',
    features: ['Hospital appointments', 'GP visits', 'Social services meetings', 'Legal appointments'],
    image: 'https://images.unsplash.com/photo-1605888357467-9caf88c89f67?w=500&auto=format&fit=crop&q=80'
  },
  {
    icon: ClipboardList,
    title: 'Errands & Practical Help',
    description: 'Practical assistance with everyday tasks and errands. We help maintain independence by supporting with shopping, collections, and other practical needs.',
    features: ['Shopping with or for client', 'Post office runs', 'Prescription collection', 'General errands'],
    image: 'https://images.unsplash.com/photo-1648414749870-282b53b1548f?w=500&auto=format&fit=crop&q=80'
  },
  {
    icon: Utensils,
    title: 'Light Home Support',
    description: 'Non-personal home support to help keep things comfortable and organised. We assist with light tasks that make everyday living easier.',
    features: ['Light tidying', 'Making tea and snacks', 'Light cleaning', 'Laundry assistance'],
    image: 'https://images.unsplash.com/photo-1710952615354-3e5e9f234fc7?w=500&auto=format&fit=crop&q=80'
  },
  {
    icon: Gamepad2,
    title: 'Cognitive & Engagement Activities',
    description: 'Stimulating activities to keep the mind active and engaged. We offer enjoyable activities that promote mental wellbeing and fun.',
    features: ['Board games', 'Puzzles', 'Reading together', 'Light memory exercises'],
    image: 'https://images.pexels.com/photos/5698208/pexels-photo-5698208.jpeg?w=500&auto=compress&cs=tinysrgb'
  },
  {
    icon: Smartphone,
    title: 'Tech & Family Connection Support',
    description: 'Help staying connected with loved ones through technology. We assist with using devices to video call family, send messages, and share photos.',
    features: ['WhatsApp support', 'Video calling family', 'Sending messages', 'Sharing photos'],
    image: 'https://images.unsplash.com/photo-1694857561361-b66b3d1a6cc8?w=500&auto=format&fit=crop&q=80'
  },
  {
    icon: HomeIcon,
    title: 'Community Engagement',
    description: 'Help connecting with local groups, clubs, and activities. We support social integration and help combat feelings of isolation.',
    features: ['Activity groups', 'Social clubs', 'Religious gatherings', 'Interest-based meetups'],
    image: 'https://images.unsplash.com/photo-1768192655316-22a5eee66e28?w=500&auto=format&fit=crop&q=80'
  },
  {
    icon: Sparkles,
    title: 'Friendly Check-ins',
    description: 'Regular wellbeing check-ins to ensure everything is going well. A reassuring touchpoint that families and individuals can rely on.',
    features: ['Phone check-ins', 'Brief home visits', 'Wellbeing monitoring', 'Family updates'],
    image: 'https://images.unsplash.com/photo-1605888357467-9caf88c89f67?w=500&auto=format&fit=crop&q=80'
  },
];

const benefits = [
  { icon: Heart, title: 'Genuine Care', description: 'Every companion is selected for their warmth and compassion.' },
  { icon: Clock, title: 'Flexible Scheduling', description: 'Services arranged around your schedule and preferences.' },
  { icon: Users, title: 'Matched Companions', description: 'We carefully match companions based on interests and personality.' },
];

export const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-white" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
                Our Services
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1E4F8A] leading-tight mb-6">
                Supporting Wellbeing, Independence and Connection
              </h1>
              <p className="text-lg text-[#4A5568] leading-relaxed mb-8">
                AERW Lifestyle offers a comprehensive range of non-regulated companionship and lifestyle 
                support services designed to enhance quality of life, maintain independence, and create 
                meaningful connections.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center bg-[#1E4F8A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#163A66] transition-colors"
                data-testid="services-hero-cta"
              >
                Discuss Your Needs
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-[#3A7BD5]/10 rounded-3xl transform rotate-3" />
              <img
                src={SERVICE_HERO}
                alt="AERW Lifestyle Services"
                className="relative rounded-3xl shadow-2xl w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Strip */}
      <section className="py-12 bg-[#1E4F8A]" data-testid="benefits-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{benefit.title}</h3>
                  <p className="text-sm text-white/70">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-[#F7F9FC]" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
              What We Offer
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-4">
              Our Companionship Services
            </h2>
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Each service is delivered with warmth, professionalism, and a genuine commitment to your wellbeing.
            </p>
          </motion.div>

          <div className="space-y-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden bg-white border-[#E2E8F0] hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className={`grid lg:grid-cols-2 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                      <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-64 lg:h-full object-cover"
                        />
                      </div>
                      <div className={`p-8 lg:p-12 flex flex-col justify-center ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                        <div className="w-14 h-14 bg-[#F7F9FC] rounded-2xl flex items-center justify-center mb-6">
                          <service.icon className="w-7 h-7 text-[#3A7BD5]" />
                        </div>
                        <h3 className="font-serif text-2xl text-[#1E4F8A] mb-4">{service.title}</h3>
                        <p className="text-[#4A5568] leading-relaxed mb-6">{service.description}</p>
                        <ul className="grid sm:grid-cols-2 gap-2">
                          {service.features.map((feature, fIndex) => (
                            <li key={fIndex} className="flex items-center gap-2 text-sm text-[#4A5568]">
                              <span className="w-1.5 h-1.5 bg-[#3A7BD5] rounded-full" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tailored Plans */}
      <section className="py-20 md:py-28 bg-white" data-testid="tailored-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <Sparkles className="w-12 h-12 text-[#3A7BD5] mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-6">
              Tailored Companionship Plans
            </h2>
            <p className="text-lg text-[#4A5568] mb-8">
              Every individual is unique. We create bespoke companionship arrangements designed around 
              your specific preferences, schedule, and lifestyle needs. Contact us to discuss a tailored 
              plan that works perfectly for you.
            </p>
            <Link
  to="/contact#consultation-form"
  className="inline-flex items-center bg-[#1E4F8A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#163A66]"
>
  Request a Tailored Plan
  <ArrowRight className="w-5 h-5 ml-2" />
</Link>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-[#F7F9FC]" data-testid="services-notice">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp}>
            <Card className="bg-white border-[#E2E8F0]">
              <CardContent className="p-8 text-center">
                <h3 className="font-semibold text-lg text-[#1A1A1A] mb-4">Important Information</h3>
                <p className="text-[#4A5568] leading-relaxed">
                  <strong>Please note:</strong> AERW Lifestyle provides non-regulated companionship and 
                  lifestyle support services only. We do not provide personal care (washing, dressing, 
                  toileting), medical care, medication administration, or any CQC-regulated services. 
                  Our focus is on social companionship, emotional wellbeing, and everyday lifestyle assistance.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-[#4A5568] mb-8">
              Contact us today for a free consultation to discuss your companionship needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
  to="/contact#consultation-form"
  className="inline-flex items-center justify-center bg-[#1E4F8A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#163A66]"
>
  Book a Consultation
  <ArrowRight className="w-5 h-5 ml-2" />
</Link>

<Link
  to="/pricing"
  className="inline-flex items-center justify-center border-2 border-[#1E4F8A] text-[#1E4F8A] px-8 py-4 rounded-full font-medium hover:bg-[#F7F9FC]"
>
  View Pricing
</Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center border-2 border-[#1E4F8A] text-[#1E4F8A] px-8 py-4 rounded-full font-medium hover:bg-[#F7F9FC] transition-colors"
              >
                View Pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
